/**
 * Observability Scrubbing
 *
 * What the manager applies to everything it hands a provider, so nothing
 * personal or secret leaves the device whichever provider is plugged in:
 *
 * - query strings are removed from URLs and paths inside any string
 *   (`/workouts?token=abc` → `/workouts`), since a query can carry tokens,
 *   emails or search text
 * - values under sensitive keys (password, token, secret, authorization,
 *   cookie, email, phone) are replaced with `[redacted]`
 * - a user is reduced to its id
 *
 * Errors, dates and other class instances are passed through untouched: a
 * provider reads an error's name, message and stack itself.
 */

import type { ObservabilityUser } from '../../types/observability';

export const REDACTED = '[redacted]';

const SENSITIVE_KEY =
  /password|passwd|passphrase|token|secret|authori[sz]ation|cookie|e-?mail|phone/i;

// A URL (scheme://host…) or a path (/…) followed by `?` and a query.
const URL_WITH_QUERY = /((?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s?#]*)\?[^\s#]+/gi;

const MAX_DEPTH = 6;

/** Removes query strings from every URL or path inside the string. */
export function stripQueryStrings(value: string): string {
  return value.replace(URL_WITH_QUERY, '$1');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function scrubValue(
  value: unknown,
  depth: number,
  seen: WeakSet<object>
): unknown {
  if (typeof value === 'string') return stripQueryStrings(value);
  if (depth >= MAX_DEPTH) return value;

  if (Array.isArray(value)) {
    if (seen.has(value)) return value;
    seen.add(value);
    return value.map((item) => scrubValue(item, depth + 1, seen));
  }

  if (isPlainObject(value)) {
    if (seen.has(value)) return value;
    seen.add(value);
    const scrubbed: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      scrubbed[key] = SENSITIVE_KEY.test(key)
        ? REDACTED
        : scrubValue(item, depth + 1, seen);
    }
    return scrubbed;
  }

  return value;
}

/** A scrubbed copy of a context, data or tags record; the input is not changed. */
export function scrubRecord<T extends Record<string, unknown>>(
  record: T | undefined
): T | undefined {
  if (record === undefined) return undefined;
  return scrubValue(record, 0, new WeakSet()) as T;
}

/** A user reduced to its id; `null` stays `null`. */
export function scrubUser(
  user: ObservabilityUser | null
): ObservabilityUser | null {
  return user === null ? null : { id: user.id };
}
